import { RocketOutlined, ToolOutlined } from '@ant-design/icons';
import { useLink } from '@refinedev/core';
import { Progress } from 'antd';
import { ROUTES } from '@/constants';
import { useQuickStartGuide, useSetupStatus } from '@/features/setup-tours';

type SiderProgressProps = {
  isCollapsed: boolean;
  onNavigate: () => void;
};

type ProgressBar = {
  label: string;
  icon: React.ReactNode;
  to: string;
  completed: number;
  total: number;
};

/** Progress bars at the foot of the sidebar. A bar leaves at 100%, the card with its last bar. */
export const SiderProgress = ({
  isCollapsed,
  onNavigate,
}: SiderProgressProps): React.JSX.Element | null => {
  const Link = useLink();
  const { data: quickStart } = useQuickStartGuide();
  // Gated by role: an employee gets no data, so no Setup bar.
  const { data: setupStatus } = useSetupStatus();

  const bars: Array<ProgressBar> = [
    ...(quickStart
      ? [
          {
            label: 'Tours',
            icon: <RocketOutlined />,
            to: ROUTES.QUICK_START,
            ...quickStart.progress,
          },
        ]
      : []),
    ...(setupStatus
      ? [
          {
            label: 'Setup',
            icon: <ToolOutlined />,
            to: ROUTES.SETUP,
            completed: setupStatus.progress.setUp,
            total: setupStatus.progress.total,
          },
        ]
      : []),
  ];
  const openBars = bars.filter(({ completed, total }) => total > 0 && completed < total);
  if (openBars.length === 0) return null;

  return (
    <div className="app-sider__progress">
      {openBars.map(({ label, icon, to, completed, total }) => {
        const count = `${String(completed)} of ${String(total)}`;
        const summary = `${label} · ${count}`;
        return (
          <Link
            key={label}
            to={to}
            className="app-sider__progress-bar"
            aria-label={summary}
            title={isCollapsed ? summary : undefined}
            onClick={onNavigate}
          >
            {isCollapsed ? (
              <>
                {icon}
                <span className="app-sider__progress-count">{`${String(completed)}/${String(total)}`}</span>
              </>
            ) : (
              <>
                <span>
                  <span className="app-sider__progress-title">{label}</span>
                  {` · ${count}`}
                </span>
                <Progress
                  percent={Math.round((completed / total) * 100)}
                  showInfo={false}
                  size="small"
                />
              </>
            )}
          </Link>
        );
      })}
    </div>
  );
};
